import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { SettingsNavGroup } from "@/components/templates";
import { useFinanceCleanupAccess } from "@/hooks/useFinanceCleanupAccess";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureGate, useTierGate } from "@/hooks/useTierGate";
import { canReadPayablesSettings } from "@/lib/finance/payables-settings-access";
import { canReadScanToEmail } from "@/lib/finance/scan-to-email-access";
import {
  financeSettingsLandingHref,
  settingsNavGroups,
  settingsNavSearch,
  type FinanceSettingsPlanAccess,
  type SettingsNavViewer,
} from "./settingsNav";

/**
 * The T5 settings nav (`SETTINGS_NAV`) for the signed-in viewer. It reads only the access state
 * the app shell and route gates already load (roles, plan and module toggles, cleanup access),
 * so it adds no request of its own; it only asks for the roles lookup to be repeated while it is
 * on screen (below). A gate still loading counts as closed, so an item never appears and then
 * vanishes unless the viewer's access really changed.
 */
export function useSettingsNav(): SettingsNavGroup[] {
  const { search } = useLocation();
  // Keeps the roles above current for the Accounts payable and Scan-to-email intake items: this
  // observer re-verifies them on focus and every 30 seconds while the tab is in front
  // (useAccessContext's refreshAccess), and the roles read above come from the same query, so a
  // role or membership revoked mid-session takes the item out at the next check. The decision itself stays on the cached roles, so the
  // item doesn't wait for a check on every settings page, and a failed check leaves it to the page,
  // which re-verifies too and shows an error rather than a refusal.
  usePermissions({ refreshAccess: true });
  const { viewer } = useSettingsNavViewer();
  const uiSearch = settingsNavSearch(search);
  const { isAdmin, isOrgAdmin, finance, payablesSettings, scanToEmail, accounting, financeCleanup, hr } = viewer;

  return useMemo(
    () => settingsNavGroups({ isAdmin, isOrgAdmin, finance, payablesSettings, scanToEmail, accounting, financeCleanup, hr }, uiSearch),
    [isAdmin, isOrgAdmin, finance, payablesSettings, scanToEmail, accounting, financeCleanup, hr, uiSearch],
  );
}

/**
 * Where `/finance/settings` should send this viewer (#7786). Callers wait for `loading` to clear before
 * using `href`; it is null when the viewer has no Finance settings page.
 */
export function useFinanceSettingsLanding(): { loading: boolean; href: string | null } {
  const { search } = useLocation();
  const { viewer, loading, accountingUsable } = useSettingsNavViewer();
  // The one Finance settings page with a plan gate of its own (ApprovalPolicies' FEATURE). It is read
  // here, not in the viewer the nav shares, so every settings page's nav does not subscribe to it.
  const approvalWorkflows = useFeatureGate("approval_workflows");
  const uiSearch = settingsNavSearch(search);
  const planAccess: FinanceSettingsPlanAccess = {
    accounting: accountingUsable,
    approvalWorkflows: !approvalWorkflows.isLoading && approvalWorkflows.hasFeature,
  };
  return {
    loading: loading || approvalWorkflows.isLoading,
    href: financeSettingsLandingHref(viewer, planAccess, uiSearch),
  };
}

/** The access state both the nav and the Finance settings landing are decided from. */
function useSettingsNavViewer(): { viewer: SettingsNavViewer; loading: boolean; accountingUsable: boolean } {
  const { isOrgAdmin, isSuperAdmin, canAccessModule, isLoading, orgRole, orgAppRolesDbOnly, appRolesDbOnly } = usePermissions();
  const accounting = useTierGate("accounting");
  const { allowed: financeCleanup } = useFinanceCleanupAccess();

  const isAdmin = !isLoading && (isOrgAdmin || isSuperAdmin);
  const orgAdmin = !isLoading && isOrgAdmin;
  const finance = !isLoading && canAccessModule("contributions");
  const hr = !isLoading && canAccessModule("hr");
  // The same decisions PayablesSettings and ApInboundEmail gate their content on, so the nav never
  // offers either a dead end.
  const payablesSettings = !isLoading && canReadPayablesSettings({ orgRole, orgAppRolesDbOnly, appRolesDbOnly });
  const scanToEmail = !isLoading && canReadScanToEmail({ orgRole, orgAppRolesDbOnly, appRolesDbOnly });
  // TierRouteGate redirects only when the plan includes accounting but it's switched off.
  const accountingOpen = !accounting.isLoading && !(accounting.tierQualifies && !accounting.toggleEnabled);

  return {
    viewer: { isAdmin, isOrgAdmin: orgAdmin, finance, payablesSettings, scanToEmail, accounting: accountingOpen, financeCleanup, hr },
    loading: isLoading || accounting.isLoading,
    accountingUsable: !accounting.isLoading && accounting.hasAccess,
  };
}
